/**
 * The list a violation was opened from, so its detail page can show
 * "3 / 12" and step to the previous or next one without going back —
 * Linear's ↑/↓ in the detail header.
 *
 * Kept in sessionStorage: it belongs to this tab's current browsing, must
 * survive a reload of the detail page, and should not leak between tabs
 * looking at different lists. Every access is guarded — storage can be
 * unavailable, and navigation must still work without it.
 */
const KEY = 'violationListContext';

export interface ListContext {
  ids: string[];
  /** Where to go back to, e.g. "/violations" or "/hoa/x/home/y/violations". */
  from: string;
}

export const saveListContext = (ctx: ListContext): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(ctx));
  } catch {
    // Storage unavailable: the detail page falls back to no stepping.
  }
};

export const readListContext = (): ListContext | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.ids) && typeof parsed?.from === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
};

export interface Position {
  index: number; // 0-based
  total: number;
  prevId: string | null;
  nextId: string | null;
}

/** Position of `id` in the saved list, or null if it was not opened from one. */
export const positionIn = (ctx: ListContext | null, id: string): Position | null => {
  if (!ctx) return null;
  const index = ctx.ids.indexOf(id);
  if (index === -1) return null;
  return {
    index,
    total: ctx.ids.length,
    prevId: index > 0 ? ctx.ids[index - 1] : null,
    nextId: index < ctx.ids.length - 1 ? ctx.ids[index + 1] : null,
  };
};
